/**
 * Turns any failure from the askRichard endpoints into one plain sentence a business reader can
 * act on. Nothing that reaches a reader may carry an HTTP status line, a Node stack trace, or the
 * name of an internal service, because none of those tell them what to do next.
 */

/** Loss of the browser's own connection to the reporting service. */
const CLIENT_NETWORK_PATTERNS = [
  /failed to fetch/i,
  /load failed/i,
  /networkerror/i,
  /network request failed/i,
  /err_(?:connection|network|internet|name_not_resolved)/i,
];

/** Loss of a hop behind the reporting service (its own fetch to the data gateway). */
const UPSTREAM_PATTERNS = [/fetch failed/i, /\bupstream\b/i, /econnrefused/i, /socket hang ?up/i, /etimedout/i];

const OFFLINE_MESSAGE = 'askRichard is offline right now. Try again in a few minutes.';
const GENERIC_MESSAGE = 'askRichard could not answer that just now. Ask the question again.';
/** Exported so the persistent fault banner (not just the transient toast this file otherwise
 *  feeds) can show the same sentence once it knows the stream failed with a lost session. */
export const SIGNED_OUT_MESSAGE = 'Your sign-in has expired. Sign in again, then ask the question once more.';
const NO_ACCESS_MESSAGE = 'You do not have access to askRichard. Ask the data team to enable it for your account.';
const BUSY_MESSAGE = 'askRichard is handling too many questions right now. Wait a moment and ask again.';
const TOO_LONG_MESSAGE = 'askRichard took too long and stopped. Ask again, or narrow the question to a shorter period.';
const SERVER_FAILED_MESSAGE = 'askRichard could not finish this question. Try again in a moment.';
const STOPPED_MESSAGE = 'Answer stopped.';

/**
 * Prose shapes that are machine output rather than an explanation: an HTTP status line, a Node or
 * browser stack frame, a JSON error envelope, or a bare error class name. Any of these means the
 * upstream message is not fit to show, so the status-derived sentence is used instead.
 */
const MACHINE_PROSE_PATTERNS = [
  /^HTTP\s+\d{3}\b/i,
  /^\s*\d{3}\s*[-:]/,
  /\bat\s+[\w$.<>]+\s*\([^)]*:\d+:\d+\)/,
  /^\s*at\s+\/?[\w./-]+:\d+:\d+/m,
  /\b(?:TypeError|ReferenceError|SyntaxError|RangeError|AssertionError|AggregateError)\b/,
  /^[[{]/,
  /<\/?(?:html|body|pre|title)\b/i,
  /\b(?:ECONNRESET|EPIPE|ENOTFOUND|ERR_[A-Z_]+)\b/,
];

/**
 * Internal names a reader has no way to act on. The service that is down is not the one they
 * think they are using, so naming it moves the problem further away rather than closer.
 */
const INTERNAL_NAME_PATTERNS = [
  /\bLE[- ]?Data\b/i,
  /\bMCP\b/i,
  /\breporting service\b/i,
  /\bsvc[-_][\w-]+/i,
  /\bbigquery\b/i,
  /\blux-analytics(?:-prod)?\b/i,
  /\bbedrock\b/i,
  /\banthropic\b/i,
  /\blocalhost(?::\d+)?\b/i,
  /\bhttps?:\/\//i,
];

/**
 * The reason phrases browsers and proxies put on the status line. On their own they restate the
 * code and explain nothing, so they are treated as machine output rather than server prose.
 */
const HTTP_REASON_PHRASES = new Set(
  [
    'bad request',
    'unauthorized',
    'payment required',
    'forbidden',
    'not found',
    'method not allowed',
    'not acceptable',
    'request timeout',
    'conflict',
    'gone',
    'payload too large',
    'unprocessable entity',
    'too many requests',
    'internal server error',
    'not implemented',
    'bad gateway',
    'service unavailable',
    'gateway timeout',
    'error',
    'unknown error',
    'request failed',
  ].map((phrase) => phrase.toLowerCase()),
);

function isMachineProse(message: string): boolean {
  if (HTTP_REASON_PHRASES.has(message.toLowerCase().replace(/[.!]$/, ''))) {
    return true;
  }
  return MACHINE_PROSE_PATTERNS.some((pattern) => pattern.test(message));
}

function namesInternals(message: string): boolean {
  return INTERNAL_NAME_PATTERNS.some((pattern) => pattern.test(message));
}

/** The sentence a status code alone justifies, before any server prose is considered. */
function messageForStatus(status: number | undefined): string | null {
  if (!status || !Number.isFinite(status)) {
    return null;
  }
  if (status === 401) {
    return SIGNED_OUT_MESSAGE;
  }
  if (status === 403) {
    return NO_ACCESS_MESSAGE;
  }
  if (status === 408 || status === 504 || status === 524) {
    return TOO_LONG_MESSAGE;
  }
  if (status === 429) {
    return BUSY_MESSAGE;
  }
  if (status === 502 || status === 503) {
    return OFFLINE_MESSAGE;
  }
  if (status >= 500) {
    return SERVER_FAILED_MESSAGE;
  }
  if (status >= 400) {
    return GENERIC_MESSAGE;
  }
  return null;
}

function classifyOfflineMessage(message: string): string | null {
  if (UPSTREAM_PATTERNS.some((pattern) => pattern.test(message))) {
    return OFFLINE_MESSAGE;
  }
  if (CLIENT_NETWORK_PATTERNS.some((pattern) => pattern.test(message))) {
    return OFFLINE_MESSAGE;
  }
  return null;
}

/**
 * Keeps server prose only when it reads as an explanation. A refusal the gateway worded for a
 * reader survives; anything that looks like machine output or names an internal system is replaced
 * by the sentence the status code justifies.
 */
function readerFacingProse(message: string, status?: number): string {
  const trimmed = message.trim();
  if (!trimmed) {
    return messageForStatus(status) ?? GENERIC_MESSAGE;
  }

  const offlineMessage = classifyOfflineMessage(trimmed);
  if (offlineMessage) {
    return offlineMessage;
  }

  if (isMachineProse(trimmed) || namesInternals(trimmed) || trimmed.length > 300) {
    return messageForStatus(status) ?? GENERIC_MESSAGE;
  }

  return trimmed;
}

function readStatus(error: unknown): number | undefined {
  if (error && typeof error === 'object') {
    const candidate = (error as { status?: unknown; statusCode?: unknown }).status;
    if (typeof candidate === 'number' && Number.isFinite(candidate)) {
      return candidate;
    }
    const alternate = (error as { statusCode?: unknown }).statusCode;
    if (typeof alternate === 'number' && Number.isFinite(alternate)) {
      return alternate;
    }
  }
  return undefined;
}

/**
 * @param error anything a fetch, an SSE read or a mutation rejected with.
 * @param options.status HTTP status when the caller knows it and the error object does not carry it.
 */
export function getLeDataApiErrorMessage(error: unknown, options?: { status?: number }): string {
  if (error instanceof DOMException && error.name === 'AbortError') {
    return STOPPED_MESSAGE;
  }

  const status = options?.status ?? readStatus(error);

  if (typeof error === 'string') {
    return readerFacingProse(error, status);
  }

  if (error instanceof Error) {
    return readerFacingProse(error.message, status);
  }

  if (error && typeof error === 'object') {
    const errorRecord = error as Record<string, unknown>;
    const message = [errorRecord.message, errorRecord.error, errorRecord.detail, errorRecord.body].find(
      (value): value is string => typeof value === 'string' && value.trim().length > 0,
    );

    if (message) {
      return readerFacingProse(message, status);
    }
  }

  return messageForStatus(status) ?? GENERIC_MESSAGE;
}
